import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  History,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  DollarSign,
} from 'lucide-react';
import { format } from 'date-fns';

export const PriceHistoryModal = ({ open, onOpenChange, menuItem, onPriceUpdated }) => {
  const { api } = useAuth();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [newPrice, setNewPrice] = useState('');
  const [reason, setReason] = useState('');
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  useEffect(() => {
    if (open && menuItem) {
      setNewPrice(menuItem.base_price.toString());
      setReason('');
      setShowUpdateForm(false);
      fetchHistory();
    }
  }, [open, menuItem]);

  const fetchHistory = async () => {
    if (!menuItem?.id) return;
    try {
      const resp = await api.get(`/menu-items/${menuItem.id}/price-history`);
      setHistory(resp.data);
    } catch (error) {
      console.error('Failed to fetch price history:', error);
    }
  };

  const handleUpdatePrice = async () => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    if (price === menuItem.base_price) {
      toast.info('Price is unchanged');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/menu-items/${menuItem.id}/price`, null, {
        params: { new_price: price, reason: reason || null },
      });
      toast.success('Price updated successfully!');
      fetchHistory();
      setShowUpdateForm(false);
      onPriceUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update price');
    } finally {
      setLoading(false);
    }
  };

  const getPriceChangeIcon = (oldPrice, newPrice) => {
    if (newPrice > oldPrice) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (newPrice < oldPrice) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getPriceChangePercent = (oldPrice, newPrice) => {
    if (oldPrice === 0) return '∞';
    const change = ((newPrice - oldPrice) / oldPrice) * 100;
    return change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  };

  if (!menuItem) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Price History - {menuItem.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Price */}
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Current Price</p>
            <p className="text-3xl font-bold font-mono text-primary">
              {formatCurrency(menuItem.base_price)}
            </p>
          </div>

          {/* Update Form */}
          {showUpdateForm ? (
            <div className="space-y-3 p-4 border rounded-lg">
              <div>
                <Label>New Price</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    className="pl-10 font-mono"
                    placeholder="Enter new price"
                  />
                </div>
              </div>
              <div>
                <Label>Reason (Optional)</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Ingredient cost increase, Promotion, Seasonal adjustment"
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowUpdateForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleUpdatePrice} disabled={loading} className="flex-1">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Update Price
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setShowUpdateForm(true)} className="w-full">
              <DollarSign className="h-4 w-4 mr-2" />
              Change Price
            </Button>
          )}

          {/* History */}
          <div>
            <Label className="mb-2 block">Price Change History</Label>
            {history.length === 0 ? (
              <div className="text-center py-8 border rounded-lg">
                <History className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No price changes recorded</p>
              </div>
            ) : (
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {history.map((record) => (
                    <div key={record.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getPriceChangeIcon(record.old_price, record.new_price)}
                          <span className="font-mono text-sm">
                            {formatCurrency(record.old_price)} → {formatCurrency(record.new_price)}
                          </span>
                          <Badge variant="outline" className={
                            record.new_price > record.old_price 
                              ? 'text-green-600 border-green-500/30'
                              : 'text-red-600 border-red-500/30'
                          }>
                            {getPriceChangePercent(record.old_price, record.new_price)}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>Changed by: {record.changed_by_name}</p>
                        <p>Date: {format(new Date(record.created_at), 'MMM d, yyyy HH:mm')}</p>
                        {record.reason && (
                          <p className="text-primary">Reason: {record.reason}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
